import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ContentViewComponent from '../../../../Component/ContentViewComponent/ContentViewComponent';
import { getRoutePathname } from '../../../../Handler/RouteHandler';
import { initFormHandler } from '../../../../Handler/FormHandler';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 as Grid, Typography } from '@mui/material';
import TextFieldComponent from '../../../../Component/TextFieldComponent/TextFieldComponent';
import SelectComponent from '../../../../Component/SelectComponent/SelectComponent';
import AutocompleteComponent from '../../../../Component/AutocompleteComponent/AutocompleteComponent';
import MultipleSelectComponent from '../../../../Component/MultipleSelectComponent/MultipleSelectComponent';
import ButtonComponent from '../../../../Component/ButtonComponent/ButtonComponent';
import ApiHandler from '../../../../Handler/ApiHandler';
import { dispatch } from '../../../../App';
import { SnackbarOpen } from '../../../../Reducer/SnackBarReducer/SnackBarSlice';
import { formatDuration } from '../../../../Common/DurationCommon';
import moment from 'moment';
import Swal from 'sweetalert2';

const formatPrice = (price) => {
    const numericPrice = Number(price || 0);
    return `${numericPrice.toFixed(2)} €`;
};

const isCancelledStatus = (status) => {
    const normalized = String(status || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    return normalized.includes('annul') || normalized.includes('cancel');
};

const isConfirmedStatus = (status) => {
    const normalized = String(status || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    return normalized === 'confirme' || normalized === 'confirmed';
};

const normalizeIntegerOrNull = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const normalizeStringOrNull = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized === '' ? null : normalized;
};

const normalizeStatusToken = (status) => {
    return String(status || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
};

const normalizePlanningDuration = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = parseInt(value, 10);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const normalizeComplementIds = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }
    return Array.from(
        new Set(
            values
                .map((value) => parseInt(value, 10))
                .filter((value) => Number.isInteger(value) && value > 0)
        )
    ).sort((a, b) => a - b);
};

function CrmAppointment() {
    const [loading, setLoading] = React.useState(true);
    const [breadcrumbs, setBreadcrumbs] = React.useState({});
    const [isEdit, setIsEdit] = React.useState(true);
    const [info, setInfo] = React.useState();
    const [optionsCustomer, setOptionsCustomer] = React.useState([]);
    const [optionsPrestation, setOptionsPrestation] = React.useState([]);
    const [optionsComplement, setOptionsComplement] = React.useState([]);
    const [optionsStatus, setOptionsStatus] = React.useState([]);
    const [optionsSlots, setOptionsSlots] = React.useState([]);
    const [showQuickCustomerDialog, setShowQuickCustomerDialog] = React.useState(false);
    const [quickCustomerLoading, setQuickCustomerLoading] = React.useState(false);
    const [saveLoading, setSaveLoading] = React.useState(false);

    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [form, setForm] = React.useState({
        customerId: {
            name: "customerId",
            label: "Client",
            textHelper: "Sélectionnez un client.",
            type: "integer",
            defaultValue: "",
            options: { validation: ["required"] },
        },
        prestationId: {
            name: "prestationId",
            label: "Prestation",
            textHelper: "Sélectionnez une prestation.",
            type: "integer",
            defaultValue: "",
            options: { validation: ["required"] },
        },
        complementIds: {
            name: "complementIds",
            label: "Compléments",
            textHelper: "Optionnel. Disponibles selon la prestation choisie.",
            type: "array",
            defaultValue: [],
            options: {},
        },
        planningDuration: {
            name: "planningDuration",
            label: "Temps en plus (minutes)",
            textHelper: "Optionnel. Prioritaire sur le temps en plus de la prestation.",
            type: "integer",
            defaultValue: "",
            options: { validation: ["integer"], min: 0 },
        },
        date: {
            name: "date",
            label: "Date",
            textHelper: "Format: YYYY-MM-DD",
            type: "text",
            defaultValue: "",
            options: { validation: ["required", "date"] },
        },
        startAt: {
            name: "startAt",
            label: "Plage horaire disponible",
            textHelper: "Choisissez une heure disponible.",
            type: "text",
            defaultValue: "",
            options: { validation: ["required"] },
        },
        status: {
            name: "status",
            label: "Statut",
            textHelper: "Statut du rendez-vous.",
            type: "text",
            defaultValue: "",
            options: { validation: ["required"] },
        },
        note: {
            name: "note",
            label: "Note",
            textHelper: "Optionnel.",
            type: "text",
            defaultValue: "",
            options: { max: 255 },
        },
    });
    const handlerForm = initFormHandler(form, setForm);
    React.useEffect(() => {
        handlerForm.start();
    }, [handlerForm]);

    const [quickCustomerForm, setQuickCustomerForm] = React.useState({
        firstname: {
            name: "firstname",
            label: "Prénom",
            textHelper: "Saisissez le prénom.",
            type: "text",
            defaultValue: "",
            options: { validation: ["required"] },
        },
        lastname: {
            name: "lastname",
            label: "Nom",
            textHelper: "Saisissez le nom.",
            type: "text",
            defaultValue: "",
            options: { validation: ["required"] },
        },
        email: {
            name: "email",
            label: "Email",
            textHelper: "Saisissez l'email.",
            type: "text",
            defaultValue: "",
            options: { validation: ["required", "email"] },
        },
        telephone: {
            name: "telephone",
            label: "Téléphone",
            textHelper: "Optionnel.",
            type: "text",
            defaultValue: "",
            options: { max: 30 },
        },
    });
    const handlerQuickCustomerForm = initFormHandler(quickCustomerForm, setQuickCustomerForm);
    React.useEffect(() => {
        handlerQuickCustomerForm.start();
    }, [handlerQuickCustomerForm]);

    const loadSlots = React.useCallback((date, prestationId, complementIds = [], planningDuration = null, appointmentId = null) => {
        if (!date || !prestationId) {
            setOptionsSlots([]);
            return;
        }

        if (moment(date, 'YYYY-MM-DD', true).isValid() && moment(date).isBefore(moment().startOf('day'))) {
            setOptionsSlots([]);
            return;
        }

        ApiHandler.get(
            {
                route: 'crm_appointment_slots',
                data: {
                    date,
                    prestationId,
                    complementIds,
                    appointmentId,
                    planningDuration,
                },
            },
            (response) => {
                if (response.status === 200) {
                    setOptionsSlots(response.data.slots || []);
                } else {
                    setOptionsSlots([]);
                }
            }
        );
    }, []);

    React.useEffect(() => {
        setBreadcrumbs({
            title: 'CRM',
            context: isEdit ? 'Éditer un rendez-vous' : 'Ajouter un rendez-vous',
            description: '',
            links: [
                { label: "Calendrier des rendez-vous", path: getRoutePathname("crm_appointment_list") }
            ]
        });

        ApiHandler.get({ route: 'crm_appointment_select' }, (response) => {
            if (response.status === 200) {
                setOptionsCustomer(response.data.customers || []);
                setOptionsPrestation(response.data.prestations || []);
                const statuses = response.data.statuses || [];
                setOptionsStatus(statuses);

                if (!handlerForm.form.status?.value && statuses.length > 0) {
                    handlerForm.setValue('status', statuses[0].value);
                }
            }
        });

        setTimeout(() => setLoading(false), 700);
    }, [handlerForm, isEdit]);

    React.useEffect(() => {
        const query = new URLSearchParams(location.search);
        const queryDate = query.get('date');

        if (params.id === 'add') {
            setIsEdit(false);
            if (queryDate && !moment(queryDate).isBefore(moment().startOf('day'))) {
                handlerForm.setValue('date', queryDate);
            }
            return;
        }

        ApiHandler.get(
            {
                route: 'crm_appointment_get',
                params: { id: params.id },
            },
            (response) => {
                if (response.status === 200) {
                    const data = response.data;
                    data.date = moment(data.startAt).format('YYYY-MM-DD');
                    handlerForm.setDataApi(data);
                    setInfo(data);
                } else {
                    dispatch(
                        SnackbarOpen({
                            text:
                                response.error && response.error.message
                                    ? response.error.message
                                    : "Une erreur inattendu s'est produite.",
                            variant: "error",
                        }),
                    );
                    navigate(getRoutePathname('crm_appointment_list'));
                }
            }
        );
    }, [handlerForm, location.search, navigate, params.id]);

    React.useEffect(() => {
        const date = handlerForm.form.date?.value;
        const prestationId = handlerForm.form.prestationId?.value;
        const complementIds = handlerForm.form.complementIds?.value || [];
        const planningDuration = handlerForm.form.planningDuration?.value || null;
        const appointmentId = isEdit && info?.id ? info.id : null;
        loadSlots(date, prestationId, complementIds, planningDuration, appointmentId);
    }, [handlerForm, handlerForm.form.date?.value, handlerForm.form.prestationId?.value, handlerForm.form.complementIds?.value, handlerForm.form.planningDuration?.value, info?.id, isEdit, loadSlots]);

    React.useEffect(() => {
        const prestationId = handlerForm.form.prestationId?.value;
        const selectedPrestation = optionsPrestation.find((item) => String(item.value) === String(prestationId));
        const complements = selectedPrestation?.complements || [];
        setOptionsComplement(complements);

        const currentValues = Array.isArray(handlerForm.form.complementIds?.value) ? handlerForm.form.complementIds.value : [];
        const allowedValues = new Set(complements.map((item) => item.value));
        const nextValues = currentValues.filter((value) => allowedValues.has(value));
        if (nextValues.length !== currentValues.length) {
            handlerForm.setValue('complementIds', nextValues);
        }
    }, [handlerForm, handlerForm.form.prestationId?.value, optionsPrestation]);

    const selectedPrestation = React.useMemo(() => {
        const prestationId = handlerForm.form.prestationId?.value;
        return optionsPrestation.find((item) => String(item.value) === String(prestationId)) || null;
    }, [handlerForm.form.prestationId?.value, optionsPrestation]);

    const selectedComplements = React.useMemo(() => {
        const selectedIds = Array.isArray(handlerForm.form.complementIds?.value) ? handlerForm.form.complementIds.value : [];
        return optionsComplement.filter((item) => selectedIds.includes(item.value));
    }, [handlerForm.form.complementIds?.value, optionsComplement]);

    const hasEditChanges = React.useMemo(() => {
        if (!isEdit || !info) {
            return false;
        }

        const currentCustomerId = normalizeIntegerOrNull(handlerForm.form.customerId?.value);
        const currentPrestationId = normalizeIntegerOrNull(handlerForm.form.prestationId?.value);
        const currentStartAt = normalizeStringOrNull(handlerForm.form.startAt?.value);
        const currentPlanningDuration = normalizePlanningDuration(handlerForm.form.planningDuration?.value);
        const currentStatus = normalizeStatusToken(handlerForm.form.status?.value);
        const currentNote = normalizeStringOrNull(handlerForm.form.note?.value);
        const currentComplementIds = normalizeComplementIds(handlerForm.form.complementIds?.value);

        const initialCustomerId = normalizeIntegerOrNull(info.customerId);
        const initialPrestationId = normalizeIntegerOrNull(info.prestationId);
        const initialStartAt = normalizeStringOrNull(info.startAt);
        const initialPlanningDuration = normalizePlanningDuration(info.planningDuration);
        const initialStatus = normalizeStatusToken(info.status);
        const initialNote = normalizeStringOrNull(info.note);
        const initialComplementIds = normalizeComplementIds(info.complementIds);

        if (currentCustomerId !== initialCustomerId) return true;
        if (currentPrestationId !== initialPrestationId) return true;
        if (currentStartAt !== initialStartAt) return true;
        if (currentPlanningDuration !== initialPlanningDuration) return true;
        if (currentStatus !== initialStatus) return true;
        if (currentNote !== initialNote) return true;
        if (currentComplementIds.length !== initialComplementIds.length) return true;
        for (let i = 0; i < currentComplementIds.length; i++) {
            if (currentComplementIds[i] !== initialComplementIds[i]) {
                return true;
            }
        }

        return false;
    }, [
        handlerForm.form.complementIds?.value,
        handlerForm.form.customerId?.value,
        handlerForm.form.note?.value,
        handlerForm.form.planningDuration?.value,
        handlerForm.form.prestationId?.value,
        handlerForm.form.startAt?.value,
        handlerForm.form.status?.value,
        info,
        isEdit,
    ]);

    const summary = React.useMemo(() => {
        const mainDuration = Number(selectedPrestation?.duration || 0);
        const mainPrice = Number(selectedPrestation?.price || 0);
        const complementsDuration = selectedComplements.reduce((total, item) => total + Number(item.duration || 0), 0);
        const complementsPrice = selectedComplements.reduce((total, item) => total + Number(item.price || 0), 0);

        return {
            duration: mainDuration + complementsDuration,
            price: mainPrice + complementsPrice,
        };
    }, [selectedPrestation, selectedComplements]);

    const save = async () => {
        if (saveLoading) return;

        if (handlerForm.checkError() < 1) {
            const dateValue = handlerForm.form.date?.value;
            const startAtValue = handlerForm.form.startAt?.value;

            if (dateValue && moment(dateValue, 'YYYY-MM-DD', true).isValid() && moment(dateValue).isBefore(moment().startOf('day'))) {
                dispatch(
                    SnackbarOpen({
                        text: "Impossible de réserver une date dans le passé.",
                        variant: "error",
                    }),
                );
                return;
            }

            if (startAtValue && moment(startAtValue, 'YYYY-MM-DD HH:mm:ss', true).isValid() && moment(startAtValue).isSameOrBefore(moment())) {
                dispatch(
                    SnackbarOpen({
                        text: "Impossible de réserver un créneau dans le passé.",
                        variant: "error",
                    }),
                );
                return;
            }

            const nextStatus = handlerForm.form.status?.value;
            const currentStatus = info?.status;
            const shouldConfirmCancellation = isEdit
                && isCancelledStatus(nextStatus)
                && !isCancelledStatus(currentStatus);
            const shouldConfirmReconfirmation = isEdit
                && isCancelledStatus(currentStatus)
                && isConfirmedStatus(nextStatus);
            const shouldConfirmEdit = isEdit && hasEditChanges;

            if (shouldConfirmEdit || shouldConfirmCancellation || shouldConfirmReconfirmation) {
                const result = await Swal.fire({
                    title: shouldConfirmCancellation
                        ? "Êtes-vous sûr d'annuler le rendez-vous ?"
                        : shouldConfirmReconfirmation
                            ? "Êtes-vous sûr de confirmer le rendez-vous ?"
                            : "Êtes-vous sûr de vouloir modifier ce rendez-vous ?",
                    text: 'Un e-mail sera envoyé au client.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: shouldConfirmCancellation
                        ? 'Oui, annuler'
                        : shouldConfirmReconfirmation
                            ? 'Oui, confirmer'
                            : 'Oui, modifier',
                    cancelButtonText: 'Non',
                    reverseButtons: true,
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            setSaveLoading(true);
            handlerForm.setFormLoading(true);
            const data = handlerForm.getData();
            delete data.date;
            data.complementIds = Array.isArray(data.complementIds)
                ? data.complementIds
                    .map((value) => parseInt(value, 10))
                    .filter((value) => Number.isInteger(value) && value > 0)
                : [];

            ApiHandler.post(
                {
                    route: isEdit ? "crm_appointment_edit" : "crm_appointment_add",
                    params: isEdit ? { id: info.id } : "",
                    data: data,
                },
                (response) => {
                    if (response.status === 200 || response.status === 201) {
                        if (!isEdit && response.status === 201) {
                            navigate(getRoutePathname('crm_appointment', { id: response.data.id }));
                        }
                        dispatch(
                            SnackbarOpen({
                                text: isEdit ? "Rendez-vous modifié avec succès" : "Rendez-vous créé avec succès",
                                variant: "success",
                            }),
                        );
                    } else if (response.status === 400) {
                        handlerForm.setErrorApi(response.error);
                    } else {
                        dispatch(
                            SnackbarOpen({
                                text:
                                    response.error && response.error.message
                                        ? response.error.message
                                        : "Une erreur inattendu s'est produite.",
                                variant: "error",
                            }),
                        );
                    }
                    handlerForm.setFormLoading(false);
                    setSaveLoading(false);
                }
            );
        }
    };

    const openQuickCustomerDialog = () => {
        handlerQuickCustomerForm.reset();
        setShowQuickCustomerDialog(true);
    };

    const saveQuickCustomer = () => {
        if (handlerQuickCustomerForm.checkError() < 1) {
            setQuickCustomerLoading(true);
            handlerQuickCustomerForm.setFormLoading(true);
            const data = handlerQuickCustomerForm.getData();

            ApiHandler.post(
                {
                    route: "crm_customer_add",
                    data: {
                        firstname: data.firstname,
                        lastname: data.lastname,
                        email: data.email,
                        telephone: data.telephone || null,
                        password: "",
                    },
                },
                (response) => {
                    if (response.status === 200 || response.status === 201) {
                        const customerId = response.data?.id;
                        const customerLabel =
                            `${(data.lastname || '').trim()} ${(data.firstname || '').trim()}`.trim() +
                            " - " +
                            String(data.email || '').toLowerCase();

                        if (customerId) {
                            setOptionsCustomer((previous) => {
                                const hasCustomer = previous.some((item) => String(item.value) === String(customerId));
                                if (hasCustomer) return previous;
                                const next = [...previous, { value: customerId, label: customerLabel }];
                                return next.sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
                            });
                            handlerForm.setValue('customerId', customerId);
                            handlerForm.setValueText('customerId', customerLabel);
                            handlerForm.setError('customerId', '');
                        }

                        setShowQuickCustomerDialog(false);
                        dispatch(
                            SnackbarOpen({
                                text: "Client créé avec succès",
                                variant: "success",
                            }),
                        );
                    } else if (response.status === 400) {
                        handlerQuickCustomerForm.setErrorApi(response.error);
                    } else {
                        dispatch(
                            SnackbarOpen({
                                text:
                                    response.error && response.error.message
                                        ? response.error.message
                                        : "Une erreur inattendu s'est produite.",
                                variant: "error",
                            }),
                        );
                    }

                    setQuickCustomerLoading(false);
                    handlerQuickCustomerForm.setFormLoading(false);
                },
            );
        }
    };

    return (
        <ContentViewComponent loading={loading} breadcrumbs={breadcrumbs}>
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 12, md: isEdit ? 12 : 8, lg: isEdit ? 12 : 8, xl: isEdit ? 12 : 8 }}>
                    <AutocompleteComponent id={"customerId"} options={optionsCustomer} handler={handlerForm} disabled={isEdit} />
                </Grid>
                {!isEdit && (
                    <Grid
                        size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'center', md: 'center' },
                            justifyContent: { xs: 'center', md: 'center' },
                        }}
                    >
                        <Box sx={{ px: { xs: 1, md: 0 }, pb: { xs: 0.5, md: 0 } }}>
                            <ButtonComponent
                                outlined
                                label={"Ajouter nouveau client"}
                                style={{marginBottom: '15px'}}
                                onClick={openQuickCustomerDialog}
                            />
                        </Box>
                    </Grid>
                )}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <AutocompleteComponent id={"prestationId"} options={optionsPrestation} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <MultipleSelectComponent id={"complementIds"} options={optionsComplement} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <TextFieldComponent id={"planningDuration"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <TextFieldComponent id={"date"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <SelectComponent id={"startAt"} options={optionsSlots} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <SelectComponent id={"status"} options={optionsStatus} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <TextFieldComponent id={"note"} handler={handlerForm} />
                </Grid>
                {!!selectedPrestation && (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Box sx={{ border: '1px solid #e7e9ef', borderRadius: '10px', p: 1.5, backgroundColor: '#f9fbff' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, color: '#374151' }}>
                                Récapitulatif
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography sx={{ fontSize: 13, color: '#111827' }}>{selectedPrestation.label}</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                                    {formatDuration(selectedPrestation.duration)} · {formatPrice(selectedPrestation.price)}
                                </Typography>
                            </Box>
                            {selectedComplements.map((item) => (
                                <Box key={`summary-complement-${item.value}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                                    <Typography sx={{ fontSize: 12, color: '#4b5563' }}>+ {item.label}</Typography>
                                    <Typography sx={{ fontSize: 12, color: '#4b5563', whiteSpace: 'nowrap' }}>
                                        +{formatDuration(item.duration)} · +{formatPrice(item.price)}
                                    </Typography>
                                </Box>
                            ))}
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #cfd6e2', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Total</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
                                    {formatDuration(summary.duration)} · {formatPrice(summary.price)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                )}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <ButtonComponent
                        style={{ width: '100%', marginTop: '20px' }}
                        label={"Enregistrer"}
                        onClick={save}
                        loading={saveLoading}
                        disabled={saveLoading}
                    />
                </Grid>
            </Grid>

            <Dialog
                open={showQuickCustomerDialog}
                maxWidth={"md"}
                fullWidth
                onClose={() => {
                    if (!quickCustomerLoading) setShowQuickCustomerDialog(false);
                }}
            >
                <DialogTitle style={{ fontSize: 15 }}>
                    Ajouter un client
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TextFieldComponent id={"firstname"} handler={handlerQuickCustomerForm} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TextFieldComponent id={"lastname"} handler={handlerQuickCustomerForm} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextFieldComponent id={"email"} handler={handlerQuickCustomerForm} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextFieldComponent id={"telephone"} handler={handlerQuickCustomerForm} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonComponent
                        disabled={quickCustomerLoading}
                        color={"var(--greytext)"}
                        label={"Annuler"}
                        onClick={() => setShowQuickCustomerDialog(false)}
                    />
                    <ButtonComponent
                        loading={quickCustomerLoading}
                        label={"Créer le client"}
                        onClick={saveQuickCustomer}
                    />
                </DialogActions>
            </Dialog>
        </ContentViewComponent>
    );
}

export default CrmAppointment;
